import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calculator, Loader2, Lock, NotebookPen } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { MEMBER_NAV } from '../../constants/nav';
import { fetchInstruments, fetchFeatureAccess, InstrumentPreset } from '../../features/journal/api/journal';
import { calcLot } from '../../features/tools/calc';

const inputClass = 'w-full bg-[#050914] border border-amber-400/30 rounded-lg px-3 py-2 text-white placeholder:text-white/30 focus:border-amber-400 focus:ring-1 focus:ring-amber-400/50 outline-none transition-colors text-sm';
const labelClass = 'block text-xs uppercase tracking-wider text-white/50 mb-1.5';
const num = (s: string) => (s.trim() === '' ? 0 : Number(s));

function Row({ label, value, strong, accent }: { label: string; value: React.ReactNode; strong?: boolean; accent?: boolean }) {
  return (
    <div className={`flex items-center justify-between px-4 py-2.5 ${strong ? 'bg-cyan-400/10' : ''}`}>
      <span className="text-sm text-white/60">{label}</span>
      <span className={`text-sm font-medium ${accent ? 'text-cyan-400' : strong ? 'text-white text-base' : 'text-white/90'}`}>{value}</span>
    </div>
  );
}

export default function LotCalculatorPage() {
  const navigate = useNavigate();
  const [instruments, setInstruments] = useState<InstrumentPreset[]>([]);
  const [access, setAccess] = useState<boolean | null>(null);
  const [symbol, setSymbol] = useState('XAUUSD');
  const [equity, setEquity] = useState('1000');
  const [riskPct, setRiskPct] = useState('2');
  const [slPips, setSlPips] = useState('50');
  const [side, setSide] = useState<'BUY' | 'SELL'>('BUY');
  const [entry, setEntry] = useState('');
  const [adv, setAdv] = useState(false);
  const [sustainMin, setSustainMin] = useState('2000');

  useEffect(() => {
    Promise.all([fetchInstruments(), fetchFeatureAccess()])
      .then(([instr, acc]) => { setInstruments(instr); setAccess(acc.kalkulator_lot); })
      .catch(() => setAccess(false));
  }, []);

  const preset = useMemo(() => instruments.find((i) => i.symbol === symbol), [instruments, symbol]);

  const useInJournal = () => {
    if (!result) return;
    localStorage.setItem('cuan_journal_prefill', JSON.stringify({
      instrument: symbol,
      trade_side: side.toLowerCase(),
      entry_price: entry ? Number(entry) : null,
      stop_loss: result.slPrice,
      take_profit: result.tpPrice,
      lot: result.lotFinal >= 0.01 ? result.lotFinal : null,
    }));
    toast.success('Mengirim ke Jurnal...');
    navigate('/member/journal');
  };

  const result = useMemo(() => {
    if (!preset) return null;
    return calcLot({
      equity: num(equity), riskPct: num(riskPct) / 100, slPips: num(slPips), side, entry: entry ? Number(entry) : null,
      pipValue: preset.pip_value, sustainMinPips: num(sustainMin), lotMin: 0.01, pipSize: preset.pip_size,
    });
  }, [preset, equity, riskPct, slPips, side, entry, sustainMin]);

  if (access === null) return <DashboardLayout navItems={MEMBER_NAV} title="Kalkulator Lot"><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" /></div></DashboardLayout>;
  if (!access) return (
    <DashboardLayout navItems={MEMBER_NAV} title="Kalkulator Lot">
      <div data-testid="lot-calc-no-access" className="max-w-md mx-auto text-center py-20">
        <Lock className="w-10 h-10 text-white/30 mx-auto mb-4" />
        <p className="text-white/60">Anda belum memiliki akses ke Kalkulator Lot. Hubungi admin.</p>
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout navItems={MEMBER_NAV} title="Kalkulator Lot">
      <div className="max-w-4xl" data-testid="lot-calc-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-amber-400/10 border border-amber-400/30 flex items-center justify-center"><Calculator className="w-5 h-5 text-amber-400" strokeWidth={1.5} /></div>
          <div><p className="text-xs uppercase tracking-[0.2em] text-amber-400">Risk Management</p><h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">Kalkulator Lot</h2></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input */}
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 space-y-4">
            <p className="text-xs uppercase tracking-wider text-amber-400/80">Input (isi kolom kuning)</p>
            <div><label className={labelClass}>Pair / Instrumen</label>
              <select data-testid="lot-symbol" className={inputClass} value={symbol} onChange={(e) => { setSymbol(e.target.value); }}>
                {instruments.map((i) => <option key={i.symbol} value={i.symbol} className="bg-[#090D1A]">{i.symbol} — {i.label}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelClass}>Equity akun ($)</label><input data-testid="lot-equity" type="number" step="any" className={inputClass} value={equity} onChange={(e) => setEquity(e.target.value)} /></div>
              <div><label className={labelClass}>Risk per trade (%)</label><input data-testid="lot-risk" type="number" step="any" className={inputClass} value={riskPct} onChange={(e) => setRiskPct(e.target.value)} /></div>
              <div><label className={labelClass}>Jarak SL (pips)</label><input data-testid="lot-sl" type="number" step="any" className={inputClass} value={slPips} onChange={(e) => setSlPips(e.target.value)} /></div>
              <div><label className={labelClass}>Arah posisi</label><select className={inputClass} value={side} onChange={(e) => setSide(e.target.value as any)}><option value="BUY" className="bg-[#090D1A]">BUY</option><option value="SELL" className="bg-[#090D1A]">SELL</option></select></div>
              <div className="col-span-2"><label className={labelClass}>Harga entry (opsional)</label><input type="number" step="any" className={inputClass} value={entry} onChange={(e) => setEntry(e.target.value)} placeholder="untuk hitung harga SL & TP" /></div>
            </div>
            <button onClick={() => setAdv(!adv)} className="text-xs text-white/50 hover:text-white/80 transition-colors">{adv ? '− Sembunyikan asumsi' : '+ Asumsi lanjutan'}</button>
            {adv && (
              <div className="grid grid-cols-2 gap-3 pt-1 border-t border-white/5">
                <div><label className={labelClass}>Ketahanan min (pips)</label><input type="number" step="any" className={inputClass} value={sustainMin} onChange={(e) => setSustainMin(e.target.value)} /></div>
                <div><label className={labelClass}>Nilai/pip/lot ($)</label><input disabled className={inputClass + ' opacity-60'} value={preset?.pip_value ?? ''} /></div>
                <div><label className={labelClass}>Ukuran 1 pip</label><input disabled className={inputClass + ' opacity-60'} value={preset?.pip_size ?? ''} /></div>
                <div><label className={labelClass}>Contract size</label><input disabled className={inputClass + ' opacity-60'} value={preset?.contract_size ?? ''} /></div>
              </div>
            )}
          </div>

          {/* Hasil */}
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden divide-y divide-white/5" data-testid="lot-result">
            <p className="text-xs uppercase tracking-wider text-cyan-400/80 px-4 py-3">Hasil perhitungan</p>
            <Row label="Risiko dalam dolar ($)" value={`$${result?.risk$ ?? 0}`} />
            <Row label="Lot dari risiko" value={result?.lotRisk ?? 0} />
            <Row label="Lot maksimal ketahanan" value={result?.lotSustain ?? 0} />
            <Row label="LOT FINAL (pakai ini)" value={<span data-testid="lot-final">{result?.lotFinal ?? 0}</span>} strong accent />
            <Row label="Pembatas lot" value={result?.limiter ?? '—'} />
            <Row label="Status" value={<span className={result?.canEntry ? 'text-emerald-400' : 'text-red-400'}>{result?.canEntry ? 'OK' : 'JANGAN ENTRY'}</span>} />
            <Row label="Risiko aktual ($)" value={`$${result?.riskActual$ ?? 0}`} />
            <Row label="Risiko aktual (% equity)" value={`${((result?.riskActualPct ?? 0) * 100).toFixed(2)}%`} />
            <Row label="TP (pips) — R:R 1:1" value={result?.tpPips ?? 0} />
            <Row label="Potensi profit di TP ($)" value={`$${result?.profitTP$ ?? 0}`} />
            <Row label="Ketahanan dgn lot final (pips)" value={result?.sustainFinalPips ?? '—'} />
            <Row label="Harga SL" value={result?.slPrice ?? '—'} />
            <Row label="Harga TP (1:1)" value={result?.tpPrice ?? '—'} />
            <div className="p-3">
              <button data-testid="lot-use-in-journal" onClick={useInJournal} disabled={!result?.canEntry} className="w-full bg-cyan-400 text-[#050914] font-medium px-4 py-2.5 rounded-lg transition-colors hover:bg-cyan-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                <NotebookPen className="w-4 h-4" strokeWidth={2} /> Pakai di Jurnal
              </button>
              {!result?.canEntry && <p className="text-[11px] text-white/40 text-center mt-2">Lot final di bawah minimum — perbesar equity atau perkecil SL.</p>}
            </div>
          </div>
        </div>
        <p className="text-xs text-white/40 mt-4">Lot final = yang LEBIH KECIL antara lot dari risiko dan lot maksimal ketahanan dana, dibulatkan ke bawah ke kelipatan lot minimum. Aturan CuanTrader: risiko maksimal 1–2% equity.</p>
      </div>
    </DashboardLayout>
  );
}
